// The UI language of the app. Source of truth for `<html lang dir>`, i18next and the Fluent
// direction. Order of precedence (docs/plan/12-frontend.md section 1): the user's locale from
// GET /me once loaded, else the choice persisted on this device (`bf.locale`), else French.
import type { StoragePort } from '@batiflow/client-core';
import { DEFAULT_LOCALE, LOCALES, directionOf, type AppLocale } from '@batiflow/i18n';

export const LOCALE_STORAGE_KEY = 'bf.locale';

export const isAppLocale = (value: unknown): value is AppLocale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

export interface LocaleStore {
  getLocale: () => AppLocale;
  /** Changes the language and remembers it on this device. */
  setLocale: (locale: AppLocale) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createLocaleStore(storage: StoragePort): LocaleStore {
  const listeners = new Set<() => void>();
  const saved = storage.getItem(LOCALE_STORAGE_KEY);
  let current: AppLocale = isAppLocale(saved) ? saved : DEFAULT_LOCALE;
  return {
    getLocale: () => current,
    setLocale(locale) {
      if (locale === current) return;
      current = locale;
      storage.setItem(LOCALE_STORAGE_KEY, locale);
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export { directionOf };
